import { useState } from "react";
import { toast } from "sonner";
import { Copy, Mail, Check, Send, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/common";

export default function EmailEditor({ email, onChange, compact }) {
  const [e, setE] = useState(email);
  const [busy, setBusy] = useState(false);
  const dirty = e.subject !== email.subject || e.body !== email.body || e.to_email !== email.to_email;
  const call = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data?.id) setE(r.data); toast.success(msg); onChange?.(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const save = () => call(() => api.put(`/emails/${e.id}`, e), "Draft saved");
  const approve = async () => { if (dirty) await api.put(`/emails/${e.id}`, e); call(() => api.post(`/emails/${e.id}/approve`), "Email approved"); };
  const copy = () => { navigator.clipboard.writeText(`Subject: ${e.subject}\n\n${e.body}`); toast.success("Copied to clipboard"); };
  const mailto = `mailto:${encodeURIComponent(e.to_email || "")}?subject=${encodeURIComponent(e.subject)}&body=${encodeURIComponent(e.body)}`;
  const sent = e.status === "sent";
  const tid = `email-${e.id}`;

  return (
    <div className="rounded-lg border p-4 space-y-3 bg-card" data-testid={tid}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold">{e.email_type}</span>
        {!compact && <span className="text-xs text-muted-foreground">· {e.company_name}</span>}
        <span className="ml-auto"><StatusBadge value={e.status === "draft" ? "Draft" : e.status === "approved" ? "Approved" : "Sent"} /></span>
      </div>
      <div className="grid gap-3">
        <div className="space-y-1"><Label className="text-xs">To (official public contact only)</Label>
          <Input value={e.to_email || ""} disabled={sent} onChange={(x) => setE({ ...e, to_email: x.target.value })} placeholder="procurement@company.com" data-testid={`${tid}-to`} /></div>
        <div className="space-y-1"><Label className="text-xs">Subject</Label>
          <Input value={e.subject} disabled={sent} onChange={(x) => setE({ ...e, subject: x.target.value })} data-testid={`${tid}-subject`} /></div>
        <div className="space-y-1"><Label className="text-xs">Body</Label>
          <Textarea rows={compact ? 8 : 12} value={e.body} disabled={sent} onChange={(x) => setE({ ...e, body: x.target.value })} className="font-mono text-xs leading-relaxed" data-testid={`${tid}-body`} /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        {!sent && dirty && <Button size="sm" variant="outline" onClick={save} disabled={busy} data-testid={`${tid}-save`}><Save className="h-4 w-4 mr-1" /> Save</Button>}
        {e.status === "draft" && <Button size="sm" onClick={approve} disabled={busy} data-testid={`${tid}-approve`}><Check className="h-4 w-4 mr-1" /> Approve</Button>}
        <Button size="sm" variant="outline" onClick={copy} data-testid={`${tid}-copy`}><Copy className="h-4 w-4 mr-1" /> Copy</Button>
        <Button size="sm" variant="outline" asChild><a href={mailto} data-testid={`${tid}-mailto`}><Mail className="h-4 w-4 mr-1" /> Open in mail app</a></Button>
        {e.status === "approved" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => call(() => api.post(`/emails/${e.id}/mark-sent`), "Marked as sent, follow-up scheduled")} disabled={busy} data-testid={`${tid}-mark-sent`}><Send className="h-4 w-4 mr-1" /> Mark as sent</Button>}
        {!sent && <Button size="sm" variant="ghost" className="text-rose-600 ml-auto" onClick={() => call(() => api.delete(`/emails/${e.id}`), "Draft deleted")} data-testid={`${tid}-delete`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
      {e.status !== "sent" && <p className="text-[11px] text-muted-foreground">Draft-only mode: no mailbox is connected, so the app never sends on its own. Send from your mail app, then click "Mark as sent".</p>}
    </div>
  );
}
